import { matchesLocalName } from "@/components/Inputs/CountrySelect/useCountrySearch.ts";
import {
  countryDialCodes,
  DIAL_CODE_PRIORITY,
  DialCodeEntry,
} from "@/components/Inputs/PhoneInput/countryDialCodes";
import { Country } from "@/helpers/types/types.ts";
import { MutableRefObject, RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface DialCodeOption {
  iso3: string;
  dialCode: string;
  flag: string;
  name: string;
}

interface UsePhonePrefixOptions {
  watchedCountryIso3: string | undefined;
  isPhoneEmpty: boolean;
  countries: Country[];
}

export interface UsePhonePrefixReturn {
  selectedIso3: string;
  selectedEntry: DialCodeEntry;
  isOpen: boolean;
  search: string;
  priorityOptions: DialCodeOption[];
  filteredOptions: DialCodeOption[];
  dropdownRef: MutableRefObject<HTMLDivElement | null>;
  searchRef: RefObject<HTMLInputElement>;
  setSearch: (value: string) => void;
  handleToggle: () => void;
  handleSelect: (iso3: string) => void;
  handleClickOutside: (event: Event) => void;
}

const DEFAULT_ISO3 = "HRV";

const toOption = (iso3: string, name: string, entry: DialCodeEntry | undefined): DialCodeOption => ({
  iso3,
  dialCode: entry?.dialCode ?? "",
  flag: entry?.flag ?? "🏳",
  name,
});

export const usePhonePrefix = ({
  watchedCountryIso3,
  isPhoneEmpty,
  countries,
}: UsePhonePrefixOptions): UsePhonePrefixReturn => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const [selectedIso3, setSelectedIso3] = useState<string>(() => {
    if (watchedCountryIso3 && countryDialCodes[watchedCountryIso3]) {
      return watchedCountryIso3;
    }
    return DEFAULT_ISO3;
  });

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Build options from the GraphQL country list, enriched with dial codes + flags.
  const allOptions = useMemo<DialCodeOption[]>(
    () =>
      countries
        .map((c) => toOption(c.iso3Code, c.name, countryDialCodes[c.iso3Code]))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [countries],
  );

  // Sync prefix from holder country when phone number is empty.
  useEffect(() => {
    if (isPhoneEmpty && watchedCountryIso3 && countryDialCodes[watchedCountryIso3]) {
      setSelectedIso3(watchedCountryIso3);
    }
  }, [watchedCountryIso3, isPhoneEmpty]);

  // Auto-focus search when dropdown opens.
  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) setSearch("");
      return !prev;
    });
  }, []);

  const handleSelect = useCallback((iso3: string) => {
    setSelectedIso3(iso3);
    setIsOpen(false);
    setSearch("");
  }, []);

  const handleClickOutside = useCallback((event: Event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      setSearch("");
    }
  }, []);

  const priorityOptions = useMemo<DialCodeOption[]>(
    () =>
      DIAL_CODE_PRIORITY.reduce<DialCodeOption[]>((acc, iso3) => {
        const country = countries.find((c) => c.iso3Code === iso3);
        const entry = countryDialCodes[iso3];
        if (country && entry) acc.push(toOption(iso3, country.name, entry));
        return acc;
      }, []),
    [countries],
  );

  const filteredOptions = useMemo<DialCodeOption[]>(() => {
    if (!search.trim()) return allOptions;
    const q = search.toLowerCase();
    return allOptions.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.dialCode.includes(q) ||
        matchesLocalName(o.iso3, q, currentLang),
    );
  }, [search, allOptions, currentLang]);

  const selectedEntry = countryDialCodes[selectedIso3] ?? countryDialCodes[DEFAULT_ISO3];

  return {
    selectedIso3,
    selectedEntry,
    isOpen,
    search,
    priorityOptions,
    filteredOptions,
    dropdownRef,
    searchRef: searchRef as RefObject<HTMLInputElement>,
    setSearch,
    handleToggle,
    handleSelect,
    handleClickOutside,
  };
};
